import { useState, useRef, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMencionables, aHandle } from './useMencionables'

// A textarea where "@" opens the list of people: type to narrow it, arrows and Enter (or a click) to pick.
// It inserts @first_last, which is what notifies that person once the comment is posted (menciones.py).
// `onChange` receives the text, not the event. Everything else goes to the <textarea>.
const ACTIVA = /(^|[\s(])@([\w.\-À-ɏ]*)$/u

export default function MentionTextarea({ value, onChange, onKeyDown, className = '', ...rest }) {
  const { t } = useTranslation('common')
  const gente = useMencionables()
  const ref = useRef(null)
  const caretPendiente = useRef(null)
  const [consulta, setConsulta] = useState(null)       // { inicio, texto } while an @ is being typed
  const [indice, setIndice] = useState(0)

  const opciones = useMemo(() => {
    if (!consulta) return []
    const q = aHandle(consulta.texto)
    const rank = (u) => {
      const n = aHandle(u.nombre)
      return n.startsWith(q) || u.handle.startsWith(q) ? 0 : (n.includes(q) ? 1 : 2)
    }
    return gente.filter(u => rank(u) < 2).sort((a, b) => rank(a) - rank(b)).slice(0, 6)
  }, [consulta, gente])

  useEffect(() => {
    if (caretPendiente.current != null && ref.current) {
      ref.current.setSelectionRange(caretPendiente.current, caretPendiente.current)
      caretPendiente.current = null
    }
  }, [value])

  const revisar = (texto, caret) => {
    const m = ACTIVA.exec(texto.slice(0, caret))
    if (m) { setConsulta({ inicio: caret - m[2].length - 1, texto: m[2] }); setIndice(0) } else setConsulta(null)
  }

  const elegir = (u) => {
    const caret = ref.current.selectionStart
    const nuevo = `${value.slice(0, consulta.inicio)}@${u.handle} ${value.slice(caret)}`
    caretPendiente.current = consulta.inicio + u.handle.length + 2
    setConsulta(null)
    onChange(nuevo)
    ref.current.focus()
  }

  const teclas = (e) => {
    if (consulta && opciones.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setIndice(i => (i + 1) % opciones.length); return }
      if (e.key === 'ArrowUp') { e.preventDefault(); setIndice(i => (i - 1 + opciones.length) % opciones.length); return }
      if ((e.key === 'Enter' && !e.ctrlKey && !e.metaKey) || e.key === 'Tab') { e.preventDefault(); elegir(opciones[indice]); return }
      if (e.key === 'Escape') { e.preventDefault(); setConsulta(null); return }
    }
    onKeyDown?.(e)
  }

  return (
    <div className="relative">
      <textarea
        {...rest} ref={ref} value={value} className={className}
        onChange={e => { onChange(e.target.value); revisar(e.target.value, e.target.selectionStart) }}
        onKeyDown={teclas}
        onClick={e => revisar(e.target.value, e.target.selectionStart)}
        onBlur={() => setTimeout(() => setConsulta(null), 120)}
        aria-autocomplete="list" aria-expanded={!!(consulta && opciones.length)} />
      {consulta && opciones.length > 0 && (
        <ul role="listbox" aria-label={t('mentions.people')}
          className="absolute left-0 right-0 top-full mt-1 z-40 max-h-56 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1">
          {opciones.map((u, i) => (
            <li key={u.id} role="option" aria-selected={i === indice}
              onMouseDown={e => { e.preventDefault(); elegir(u) }} onMouseEnter={() => setIndice(i)}
              className={`px-3 py-1.5 text-sm cursor-pointer flex items-center justify-between gap-3 ${i === indice ? 'bg-indigo-50' : ''}`}>
              <span className="text-gray-900 truncate">{u.nombre}</span>
              <span className="text-xs text-gray-400 shrink-0">@{u.handle}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
